/**
 * Calendar Header Component
 * View mode selector and date navigation
 */

import {
  Box,
  Button,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  Today,
  ViewModule,
  ViewWeek,
  ViewDay,
  ViewList,
  ViewAgenda,
} from '@mui/icons-material'
import { format, startOfWeek, endOfWeek } from 'date-fns'

export type CalendarViewMode = 'month' | 'week' | 'day' | 'list' | 'agenda'

interface CalendarHeaderProps {
  currentDate: Date
  viewMode: CalendarViewMode
  onViewModeChange: (mode: CalendarViewMode) => void
  onDateChange: (date: Date) => void
  onToday: () => void
}

export function CalendarHeader({
  currentDate,
  viewMode,
  onViewModeChange,
  onDateChange,
  onToday,
}: CalendarHeaderProps) {
  const handlePrevious = () => {
    const newDate = new Date(currentDate)
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1)
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7)
    } else if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() - 1)
    }
    onDateChange(newDate)
  }

  const handleNext = () => {
    const newDate = new Date(currentDate)
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1)
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7)
    } else if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + 1)
    }
    onDateChange(newDate)
  }

  const getDateRangeLabel = () => {
    if (viewMode === 'month' || viewMode === 'agenda') {
      return format(currentDate, 'MMMM yyyy')
    } else if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 0 })
      const end = endOfWeek(currentDate, { weekStartsOn: 0 })
      if (start.getMonth() === end.getMonth()) {
        return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`
      }
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`
    } else if (viewMode === 'day') {
      return format(currentDate, 'EEEE, MMMM d, yyyy')
    }
    return format(currentDate, 'MMMM yyyy')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'center' },
        gap: 2,
        mb: 3,
      }}
    >
      {/* Left: Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onToday}
          startIcon={<Today />}
        >
          Today
        </Button>
        <IconButton onClick={handlePrevious} size="small">
          <ChevronLeft />
        </IconButton>
        <IconButton onClick={handleNext} size="small">
          <ChevronRight />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, minWidth: { xs: 'auto', sm: 220 } }}>
          {getDateRangeLabel()}
        </Typography>
      </Box>

      {/* Right: View Mode Toggle */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_, value) => value && onViewModeChange(value)}
        size="small"
      >
        <ToggleButton value="month" title="Month view">
          <ViewModule sx={{ mr: { xs: 0, sm: 0.5 } }} />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Month</Box>
        </ToggleButton>
        <ToggleButton value="week" title="Week view">
          <ViewWeek sx={{ mr: { xs: 0, sm: 0.5 } }} />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Week</Box>
        </ToggleButton>
        <ToggleButton value="day" title="Day view">
          <ViewDay sx={{ mr: { xs: 0, sm: 0.5 } }} />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Day</Box>
        </ToggleButton>
        <ToggleButton value="list" title="List view">
          <ViewList sx={{ mr: { xs: 0, sm: 0.5 } }} />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>List</Box>
        </ToggleButton>
        <ToggleButton value="agenda" title="Agenda view">
          <ViewAgenda sx={{ mr: { xs: 0, sm: 0.5 } }} />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Agenda</Box>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}
